import Api from "@/services/Api";
import helpers from "@/helpers.js";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Product guid in DB (RFC UUID or legacy code-style guid, max 36 chars). */
export function isValidProductGuid(value) {
  if (value == null || typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed.length < 2 || trimmed.length > 36) return false;
  if (UUID_RE.test(trimmed)) return true;
  return /^[A-Za-z0-9._/-]+$/.test(trimmed);
}

export default {
  async getProducts(page, per_page) {
    var product_list = [];
    await Api(true)
      .post("product/product_list", { page, per_page })
      .then((r) => {
        const raw = r.data;
        let rows = [];

        if (Array.isArray(raw)) {
          // Normal case: backend returns an array
          rows = raw;
        } else if (typeof raw === "string") {
          // Fallback: backend returned a JSON-encoded string like "[{...}]"
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) rows = parsed;
          } catch (e) {
            rows = [];
          }
        } else if (Array.isArray(raw?.data)) {
          // Support paginator-style: { data: [...] }
          rows = raw.data;
        }

        product_list = rows.map((e) => {
          // if(e.vat !== null && e.price) {
          //     e.price = e.price * (1 + (e.vat / 100))
          // }
          e.label = e.name;
          return e;
        });
      });
    return product_list;
  },

  async getProductsGrid({ page = 1, per_page = 50, search = "", sort_field = "name", sort_dir = "asc", filters = {}, product_codes = null } = {}) {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters || {}).filter(([, v]) => {
        if (v === null || v === undefined) return false;
        if (Array.isArray(v)) return v.length > 0;
        return String(v).trim() !== "";
      })
    );
    const codes = Array.isArray(product_codes)
      ? product_codes.map((c) => String(c).trim()).filter(Boolean)
      : [];
    const payload = {
      page,
      per_page,
      sort_field: sort_field || "name",
      sort_dir: sort_dir || "asc",
    };
    if (codes.length) {
      payload.product_codes = codes;
      // Servers without product_codes support: single-code LIKE fallback.
      if (codes.length === 1) {
        cleanFilters.code = codes[0];
      }
    } else if (search?.trim()) {
      payload.search = search.trim();
    }
    if (Object.keys(cleanFilters).length) {
      payload.filters = cleanFilters;
    }
    const res = await Api(true).post("product/grid_list", payload);
    return res.data;
  },

  async getCategoryFilterOptions({ level1 = [], level2 = [] } = {}) {
    const res = await Api(true).post("product/category-filter-options", {
      level1: Array.isArray(level1) ? level1 : [],
      level2: Array.isArray(level2) ? level2 : [],
    });
    return res.data || { level1: [], level2: [], level4: [] };
  },

  async suggestProducts(q, limit = 12) {
    const payload = {
      q: String(q ?? "").trim(),
      limit,
    };
    const res = await Api(true).post("product/suggest", payload);
    return res.data;
  },

  async searchComponents(q, excludeGuid = null) {
    const payload = {
      q: String(q ?? "").trim(),
      limit: 30,
    };
    if (isValidProductGuid(excludeGuid)) {
      payload.exclude_guid = excludeGuid.trim();
    }
    const res = await Api(true).post("product/component-search", payload);
    return res.data;
  },

  async getOnlineSyncHealth() {
    const res = await Api(true).post("product/online-sync-health", {});
    return res.data;
  },

  async exportOnlineSyncHealth() {
    try {
      const res = await Api(true).post("product/online-sync-health/export", {}, { responseType: "blob" });
      const contentType = res?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || "Nuk u shkarkua eksporti Excel.");
      }
      this.saveBlobDownload(res, `sinkronizim_online_${new Date().toISOString().split("T")[0]}.xls`);
      return true;
    } catch (e) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.message || "Nuk u shkarkua eksporti Excel.");
        } catch (parseErr) {
          if (parseErr?.message && parseErr.message !== text) throw parseErr;
        }
      }
      throw e;
    }
  },

  async getSystemSyncHealth() {
    const res = await Api(true).post("product/system-sync-health", {});
    return res.data;
  },

  async exportSystemSyncHealth() {
    try {
      const res = await Api(true).post("product/system-sync-health/export", {}, { responseType: "blob" });
      const contentType = res?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || "Nuk u shkarkua eksporti Excel.");
      }
      this.saveBlobDownload(res, `sinkronizim_sistemi_${new Date().toISOString().split("T")[0]}.xls`);
      return true;
    } catch (e) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.message || "Nuk u shkarkua eksporti Excel.");
        } catch (parseErr) {
          if (parseErr?.message && parseErr.message !== text) throw parseErr;
        }
      }
      throw e;
    }
  },

  async exportOnlineProductListJson() {
    try {
      const res = await Api(true).post("product/online-product-list/export", {}, { responseType: "blob" });
      const contentType = res?.headers?.["content-type"] || "";
      if (contentType.includes("application/json") && res.data?.type === "application/json") {
        // error response as json blob
        const text = await res.data.text();
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) throw new Error(parsed.message);
        } catch (parseErr) {
          if (parseErr?.message) throw parseErr;
        }
      }
      this.saveBlobDownload(res, `produkte_web_${new Date().toISOString().split("T")[0]}.json`);
      return true;
    } catch (e) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.message || "Nuk u shkarkua JSON i produkteve web.");
        } catch (parseErr) {
          if (parseErr?.message && parseErr.message !== text) throw parseErr;
        }
      }
      throw e;
    }
  },

  async get(p) {
    var product = {};
    await Api(true)
      .post("product/details", p)
      .then((r) => {
        product = r.data;
      });
    return product;
  },

  async createProduct(product) {
    let payload = product;
    let config = {};

    // Check if we have image data with actual files
    if (product.product_images && product.product_images.new_images && product.product_images.new_images.length > 0) {
      const formData = new FormData();

      // 1. Append all standard fields (name, price, etc.)
      for (const key in product) {
        if (key === "product_images") continue; // Skip images for now
        if (product[key] === null || product[key] === undefined) continue;

        // If you have arrays (like categories list), you might need to stringify them or append with []
        // Simple implementation for standard fields:
        if (Array.isArray(product[key]) || typeof product[key] === "object") {
          formData.append(key, JSON.stringify(product[key]));
        } else {
          formData.append(key, product[key]);
        }
      }

      // 2. Append Image Files
      // Laravel expects arrays like: product_images[new_images][0]
      product.product_images.new_images.forEach((file, index) => {
        formData.append(`product_images[new_images][${index}]`, file);
      });
      formData.append("product_images[new_images_count]", product.product_images.new_images.length);

      // 3. Append Image Metadata
      formData.append("product_images[main_index_new]", product.product_images.main_index_new);

      // (Optional for create, but good for consistency)
      if (product.product_images.deleted_ids) {
        product.product_images.deleted_ids.forEach((id, i) => formData.append(`product_images[deleted_ids][${i}]`, id));
      }

      payload = formData;
      config = { headers: { "Content-Type": "multipart/form-data" } };
    }

    // Send Request
    await Api(true)
      .post("product/create", payload, config)
      .then((r) => {
        product = r.data;
      });

    return product;
  },

  async editProduct(product) {
    let payload = product;
    let config = {};

    // Check if we have image data to process (either new files, or deletions/main changes)
    if (
      product.product_images &&
      ((product.product_images.new_images && product.product_images.new_images.length > 0) ||
        (product.product_images.deleted_ids && product.product_images.deleted_ids.length > 0) ||
        product.product_images.main_id_existing)
    ) {
      const formData = new FormData();

      // 1. Append Standard Fields
      for (const key in product) {
        if (key === "product_images") continue;
        if (product[key] === null || product[key] === undefined) continue;

        // Stringify arrays (like attributes lists) so they can be sent via FormData
        if (Array.isArray(product[key]) || typeof product[key] === "object") {
          formData.append(key, JSON.stringify(product[key]));
        } else {
          formData.append(key, product[key]);
        }
      }

      // 2. Append New Images
      if (product.product_images.new_images) {
        product.product_images.new_images.forEach((file, index) => {
          formData.append(`product_images[new_images][${index}]`, file);
        });
        formData.append("product_images[new_images_count]", product.product_images.new_images.length);
      }

      // 3. Append Metadata
      formData.append("product_images[main_index_new]", product.product_images.main_index_new);
      formData.append("product_images[main_id_existing]", product.product_images.main_id_existing || "");

      // 4. Append Deleted IDs
      if (product.product_images.deleted_ids) {
        // Send as JSON string to be safe, or loop
        formData.append(`product_images[deleted_ids]`, JSON.stringify(product.product_images.deleted_ids));
      }

      payload = formData;
      config = { headers: { "Content-Type": "multipart/form-data" } };
    }

    // Send Request
    let response_data = {};
    try {
      const r = await Api(true).post("product/edit", payload, config);
      response_data = r.data;
    } catch (e) {
      console.error("product/edit failed", e?.response?.status, e?.response?.data || e);
      throw e;
    }

    if (!response_data || (Array.isArray(response_data) && response_data.length === 0) || !response_data.guid) {
      throw new Error("Serveri nuk ktheu produktin e ruajtur. Kontrolloni log-et dhe provoni përsëri.");
    }

    return response_data;
  },
  async getUnits() {
    var units = [];
    await Api(true)
      .post("unit/list", {})
      .then((r) => {
        units = r.data.map((e) => {
          // e.id = e.name
          e.label = e.name;
          return e;
        });
      });
    return units;
  },

  async getMaterials() {
    var materials = [];
    await Api(true)
      .post("material/list", {})
      .then((r) => {
        materials = r.data.map((e) => {
          e.label = e.name;
          return e;
        });
      });
    return materials;
  },

  async getBrands() {
    var brands = [];
    await Api(true)
      .post("brand/list", {})
      .then((r) => {
        brands = r.data.map((e) => {
          e.label = e.name;
          return e;
        });
      });
    return brands;
  },

  async getWarranties() {
    var warranties = [];
    await Api(true)
      .post("warranties/list", {})
      .then((r) => {
        warranties = r.data.map((e) => {
          e.label = e.name;
          e.id = e.guid;
          return e;
        });
      });
    return warranties;
  },

  async getVat() {
    var vat = [];
    await Api(true)
      .post("vat/list", {})
      .then((r) => {
        vat = r.data.map((e) => {
          e.label = e.value;
          e.id = e.value;
          return e;
        });
      });
    return vat;
  },

  async getProductRecipes() {
    var product_recipes = [];
    await Api(true)
      .post("product/product_recipe", {})
      .then((r) => {
        product_recipes = r.data;
      });
    return product_recipes;
  },

  async createProductRecipe(product_recipe) {
    await Api(true)
      .post("product/product_recipe/create", product_recipe)
      .then((r) => {
        product_recipe = r.data;
      });
    return product_recipe;
  },
  async editProductRecipe(product_recipe) {
    await Api(true)
      .post("product/product_recipe/edit", product_recipe)
      .then((r) => {
        product_recipe = r.data;
      });
    return product_recipe;
  },
  async get_exclusive_entity(product, only_connected = null) {
    var exclusives = {};
    await Api(true)
      .post("product/get/exclusive", { product, only_connected })
      .then((r) => {
        exclusives = r.data;
        exclusives.services_with_services_types.map((e) => {
          e.label = e.name;
          e.id = e.guid;
          return e;
        });
        exclusives.services_with_services_types = [...helpers.toTree(exclusives.services_with_services_types, "id")];
      });
    return exclusives;
  },
  async getManagers() {
    var managers = [];
    await Api(true)
      .post("product/managers/list", {})
      .then((r) => {
        managers = r.data.map((e) => {
          e.label = e.name;
          return e;
        });
      });
    return managers;
  },
  async getOriginCountries() {
    var countries = [];
    await Api(true)
      .post("product/countries/list", {})
      .then((r) => {
        countries = r.data.map((e) => {
          return {
            ...e,
            id: e.code,
            label: `${e.code} - ${e.name}`,
          };
        });
      });
    return countries;
  },
  async get_product_diaries(guid) {
    var diaries = [];
    await Api(true)
      .post("product/get/diaries", guid)
      .then((r) => {
        diaries = r.data;
      });
    return diaries;
  },
  async get_product_processes(guid) {
    var diaries = [];
    await Api(true)
      .post("product/get/processes", guid)
      .then((r) => {
        diaries = r.data;
      });
    return diaries;
  },

  async getSuppliers() {
    var suppliers = [];
    await Api(true)
      .post("supplier/list", {})
      .then((r) => {
        suppliers = r.data.map((e) => {
          e.label = e.name;
          return e;
        });
      });
    return suppliers;
  },

  saveBlobDownload(res, fallbackName) {
    let filename = fallbackName;
    const disposition = res?.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    if (match && match[1]) filename = match[1];

    let mime = res?.headers?.["content-type"] || "";
    if (!mime && /\.pdf$/i.test(filename)) {
      mime = "application/pdf";
    }
    if (mime.includes(";")) {
      mime = mime.split(";")[0].trim();
    }

    const blob = mime ? new Blob([res.data], { type: mime }) : new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  imageExtensionFromMime(mime = "") {
    const type = String(mime || "").toLowerCase().split(";")[0].trim();
    const map = {
      "image/jpeg": "jpg",
      "image/jpg": "jpg",
      "image/pjpeg": "jpg",
      "image/png": "png",
      "image/gif": "gif",
      "image/webp": "webp",
      "image/bmp": "bmp",
      "image/svg+xml": "svg",
      "image/x-icon": "ico",
      "image/vnd.microsoft.icon": "ico",
    };
    return map[type] || null;
  },

  imageExtensionFromBytes(header) {
    if (header[0] === 0xff && header[1] === 0xd8) return "jpg";
    if (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47) return "png";
    if (header[0] === 0x47 && header[1] === 0x49 && header[2] === 0x46) return "gif";
    if (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46) return "webp";
    return null;
  },

  ensureImageFilename(filename, fallbackBase, mime = "", header = null) {
    let name = String(filename || fallbackBase || "product-image").trim();
    name = name.replace(/[\\/]+/g, "_").replace(/["<>|?*]+/g, "");
    // Strip wrong html/htm endings from broken earlier downloads / browser defaults
    name = name.replace(/\.(html?|htm|php|aspx?)$/i, "");

    const knownExt = name.match(/\.([a-z0-9]{2,5})$/i);
    const hasImageExt = knownExt && /^(jpe?g|png|gif|webp|bmp|svg|ico)$/i.test(knownExt[1]);
    if (hasImageExt) return name;

    const ext =
      this.imageExtensionFromMime(mime) ||
      (header ? this.imageExtensionFromBytes(header) : null) ||
      "jpg";
    const base = knownExt ? name.slice(0, -(knownExt[0].length)) : name;
    return `${base || fallbackBase || "product-image"}.${ext}`;
  },

  async assertDownloadableImageBlob(blob, contentType = "") {
    const type = String(contentType || blob?.type || "").toLowerCase();
    if (type.includes("application/json") || type.includes("text/html") || type.startsWith("text/")) {
      const text = await blob.text();
      try {
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || "Nuk u shkarkua fotoja.");
      } catch (parseErr) {
        if (parseErr?.message && parseErr.message !== text) throw parseErr;
      }
      throw new Error("Nuk u shkarkua fotoja.");
    }

    const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const extFromBytes = this.imageExtensionFromBytes(header);
    const looksLikeImage = type.startsWith("image/") || !!extFromBytes;

    if (!looksLikeImage) {
      throw new Error("Nuk u shkarkua fotoja.");
    }

    // Re-wrap so the blob has an image MIME type (helps OS pick the right extension)
    const mime =
      (type.startsWith("image/") ? type.split(";")[0].trim() : null) ||
      (extFromBytes === "jpg"
        ? "image/jpeg"
        : extFromBytes === "png"
          ? "image/png"
          : extFromBytes === "gif"
            ? "image/gif"
            : extFromBytes === "webp"
              ? "image/webp"
              : "image/jpeg");

    const imageBlob = blob.type === mime ? blob : new Blob([blob], { type: mime });
    return { blob: imageBlob, mime, header };
  },

  async downloadProductImage({ id = null, path = null, fallbackName = null } = {}) {
    try {
      const res = await Api(true).post(
        "product/image/download",
        { id, path },
        { responseType: "blob" }
      );
      const contentType = res?.headers?.["content-type"] || "";
      const { blob, mime, header } = await this.assertDownloadableImageBlob(res.data, contentType);

      let filenameFromHeader = null;
      const disposition = res?.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
      if (match && match[1]) {
        try {
          filenameFromHeader = decodeURIComponent(match[1].replace(/['"]/g, "").trim());
        } catch {
          filenameFromHeader = match[1].replace(/['"]/g, "").trim();
        }
      }

      const filename = this.ensureImageFilename(
        filenameFromHeader || fallbackName,
        "product-image",
        mime || contentType,
        header
      );

      return { blob, filename, mime };
    } catch (e) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        await this.assertDownloadableImageBlob(data, e?.response?.headers?.["content-type"] || "");
      }
      throw e;
    }
  },

  async previewLabelExport({ product_guids = [], category_guid = null, related_entity = "products", include_descendants = false } = {}) {
    const res = await Api(true).post("product/labels/export/preview", {
      product_guids,
      category_guid,
      related_entity,
      include_descendants,
    });
    return res.data;
  },

  async resolveLabelCodes(codes = []) {
    const list = Array.isArray(codes)
      ? codes.map((c) => String(c).trim()).filter(Boolean)
      : [];
    const res = await Api(true).post("product/labels/resolve-by-codes", { codes: list });
    return res.data;
  },

  async downloadBulkImportTemplate() {
    const res = await Api(true).get("product/bulk-import/template", { responseType: "blob" });
    const contentType = res?.headers?.["content-type"] || "";
    if (contentType.includes("application/json")) {
      const text = await res.data.text();
      const parsed = JSON.parse(text);
      throw new Error(parsed?.message || "Nuk u shkarkua shablloni.");
    }
    this.saveBlobDownload(res, "produkte_ndryshim_ne_grup_template.xls");
    return true;
  },

  async previewBulkImport(file) {
    const fd = new FormData();
    fd.append("file", file);
    const res = await Api(true).post("product/bulk-import/preview", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  async applyBulkImport(file, reasonText = "") {
    const fd = new FormData();
    fd.append("file", file);
    if (reasonText) fd.append("reason_text", reasonText);
    const res = await Api(true).post("product/bulk-import/apply", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  async exportProductAttributes({ search = "", filters = {}, product_codes = null } = {}) {
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters || {}).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "")
      );
      const codes = Array.isArray(product_codes)
        ? product_codes.map((c) => String(c).trim()).filter(Boolean)
        : [];
      const payload = {};
      if (codes.length) {
        payload.product_codes = codes;
      } else if (search?.trim()) {
        payload.search = search.trim();
      }
      if (Object.keys(cleanFilters).length) {
        payload.filters = cleanFilters;
      }

      const res = await Api(true).post("product/attributes/export", payload, { responseType: "blob" });

      const contentType = res?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || "Nuk u shkarkua eksporti i atributeve.");
      }

      const fallback = `produkte_atribute_${new Date().toISOString().split("T")[0]}.csv`;
      this.saveBlobDownload(res, fallback);
      return true;
    } catch (e) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.message || "Nuk u shkarkua eksporti i atributeve.");
        } catch (parseErr) {
          if (parseErr?.message && parseErr.message !== text) throw parseErr;
        }
      }
      throw e;
    }
  },

  async exportGridCsv({
    search = "",
    filters = {},
    product_codes = null,
    sort_field = "name",
    sort_dir = "asc",
  } = {}) {
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters || {}).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "")
      );
      const codes = Array.isArray(product_codes)
        ? product_codes.map((c) => String(c).trim()).filter(Boolean)
        : [];
      const payload = {
        sort_field: sort_field || "name",
        sort_dir: sort_dir === "desc" ? "desc" : "asc",
      };
      if (codes.length) {
        payload.product_codes = codes;
      } else if (search?.trim()) {
        payload.search = search.trim();
      }
      if (Object.keys(cleanFilters).length) {
        payload.filters = cleanFilters;
      }

      const res = await Api(true).post("product/grid/export", payload, { responseType: "blob" });

      const contentType = res?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || "Nuk u shkarkua eksporti i produkteve.");
      }

      const fallback = `produkte_filtruar_${new Date().toISOString().split("T")[0]}.csv`;
      this.saveBlobDownload(res, fallback);
      return true;
    } catch (e) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.message || "Nuk u shkarkua eksporti i produkteve.");
        } catch (parseErr) {
          if (parseErr?.message && parseErr.message !== text) throw parseErr;
        }
      }
      throw e;
    }
  },

  async exportLabels({ product_guids = [], category_guid = null, related_entity = "products", include_descendants = false, per_page = null } = {}) {
    try {
      const payload = {
        product_guids,
        category_guid,
        related_entity,
        include_descendants,
      };
      if (per_page && typeof per_page === "object") {
        payload.per_page = per_page;
      }

      const res = await Api(true).post("product/labels/export", payload, { responseType: "blob" });

      const contentType = res?.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || "Nuk u shkarkua eksporti i etiketave.");
      }

      const fallback =
        category_guid != null
          ? `etiketa_kategori_${new Date().toISOString().split("T")[0]}.pdf`
          : `etiketa_produkte_${new Date().toISOString().split("T")[0]}.pdf`;
      this.saveBlobDownload(res, fallback);
      return true;
    } catch (e) {
      const data = e?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed?.message || "Nuk u shkarkua PDF i etiketave.");
        } catch (parseErr) {
          if (parseErr?.message && parseErr.message !== text) throw parseErr;
        }
      }
      throw e;
    }
  },

  async resyncFinanca({ product_ids, guids, source } = {}) {
    const payload = {};
    if (Array.isArray(product_ids) && product_ids.length) {
      payload.product_ids = product_ids.map((id) => Number(id)).filter(Boolean);
    }
    if (Array.isArray(guids) && guids.length) {
      payload.guids = guids.map((g) => String(g).trim()).filter(Boolean);
    }
    if (source) payload.source = source;
    const res = await Api(true).post("product/resync-financa", payload);
    return res.data;
  },
};
